import styled from '@emotion/styled';
import { forwardRef, InputHTMLAttributes } from 'react';

import { mq } from '@/utility/mediaQuery';
import { defaultTheme } from '@/utility/theme';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  width?: string;
  height?: string;
  bgColor?: keyof typeof defaultTheme.background;
  textAlign?: string;
  disabled?: boolean;
  fontSize?: keyof typeof defaultTheme.font.size;
  hasError?: boolean;
}

const getFontSize = (fontSize?: keyof typeof defaultTheme.font.size) => {
  if (fontSize) {
    return ['xs', 's'].includes(fontSize)
      ? defaultTheme.font.size.m
      : defaultTheme.font.size[fontSize];
  }
  return defaultTheme.font.size.m;
};

const getTransformScale = (fontSize?: keyof typeof defaultTheme.font.size) => {
  // デフォルトのサイズをsとする
  if (!fontSize || fontSize === 's') {
    return 14 / 16;
  }
  if (fontSize === 'xs') {
    return 12 / 16;
  }
  return 1;
};

// widthをheightが縮小される分を拡大する
const getWidthHeight = (fontSize?: keyof typeof defaultTheme.font.size) => {
  // デフォルトのサイズをsとする
  if (!fontSize || fontSize === 's') {
    return (16 / 14) * 100;
  }
  if (fontSize === 'xs') {
    return (16 / 12) * 100;
  }
  return 100;
};

const getBgColor = (
  bgColor?: keyof typeof defaultTheme.background,
  hasError?: boolean,
) => {
  if (hasError) {
    return defaultTheme.background.error;
  }
  if (bgColor) {
    return defaultTheme.background[bgColor];
  }
  return defaultTheme.background.white;
};

const StyledInput = styled.input((props: InputProps) =>
  mq({
    width: `${getWidthHeight(props.fontSize)}%`,
    height: `${getWidthHeight(props.fontSize)}%`,
    fontSize: getFontSize(props.fontSize),
    transform: `scale(${getTransformScale(props.fontSize)})`,
    textAlign: props.textAlign ? props.textAlign : '',
    transformOrigin: 'left top',
    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
      appearance: 'none',
      margin: '0',
    },
    appearance: 'textfield',
    '&::placeholder': {
      color: defaultTheme.text.gray,
    },
  }),
);

const StyledDiv = styled.div(
  (props: {
    height?: string;
    width?: string;
    bgColor?: keyof typeof defaultTheme.background;
    fontSize?: keyof typeof defaultTheme.font.size;
    hasError?: boolean;
  }) =>
    mq({
      height: props.height ? props.height : '',
      width: props.width ? props.width : '100%',
      fontSize: props.fontSize
        ? defaultTheme.font.size[props.fontSize]
        : defaultTheme.font.size.s,
      display: 'inline-block',
      border: `1px solid ${
        props.hasError
          ? defaultTheme.border.color.error
          : defaultTheme.border.color.mediumGray
      }`,
      padding: '0.5em 1em',
      borderRadius: '4px',
      backgroundColor: getBgColor(props.bgColor, props.hasError),
      overflow: 'hidden',
    }),
);

const Input = forwardRef<any, InputProps>((props, ref) => {
  return (
    <StyledDiv
      height={props.height}
      width={props.width}
      bgColor={props.bgColor}
      fontSize={props.fontSize}
      hasError={props.hasError}
    >
      <StyledInput {...props} ref={ref} />
    </StyledDiv>
  );
});

export default Input;
